import { useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import AuthContext from '../context/authContext';
import useHttp from './useHttp';

const useGoogleReview = () => {
  const authCtx = useContext(AuthContext);
  const { sendRequest } = useHttp();

  const getReviews = useCallback(
    async ({
      status = null,
      minStars = null,
      maxStars = null,
      branchId = null,
      googleLocationName = null,
      entertainmentId = null,
      hasReply = null,
      search = null,
      page = 1,
      limit = 50,
    } = {}) => {
      try {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        if (minStars) params.append('minStars', String(minStars));
        if (maxStars) params.append('maxStars', String(maxStars));
        if (branchId) params.append('branchId', branchId);
        if (googleLocationName) params.append('googleLocationName', googleLocationName);
        if (entertainmentId) params.append('entertainmentId', entertainmentId);
        if (hasReply !== null && hasReply !== undefined && hasReply !== '') {
          params.append('hasReply', String(hasReply));
        }
        if (search) params.append('search', search);
        params.append('page', String(page));
        params.append('limit', String(limit));

        return await sendRequest({
          url: `/api/v1/googleReview?${params.toString()}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Laden der Google-Rezensionen');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const getReviewById = useCallback(
    async reviewId => {
      try {
        return await sendRequest({
          url: `/api/v1/googleReview/${reviewId}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Laden der Google-Rezension');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const updateStatus = useCallback(
    async ({ reviewId, status }) => {
      try {
        return await sendRequest({
          url: `/api/v1/googleReview/${reviewId}/status`,
          method: 'PATCH',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: { status },
        });
      } catch (err) {
        toast.error('Fehler beim Aktualisieren des Status');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const updateInternalNote = useCallback(
    async ({ reviewId, internalNote }) => {
      try {
        return await sendRequest({
          url: `/api/v1/googleReview/${reviewId}/internalNote`,
          method: 'PATCH',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: { internalNote },
        });
      } catch (err) {
        toast.error('Fehler beim Speichern der internen Notiz');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const generateAiSuggestion = useCallback(
    async ({ reviewId }) => {
      try {
        return await sendRequest({
          url: `/api/v1/googleReview/${reviewId}/aiSuggestion`,
          method: 'POST',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
        });
      } catch (err) {
        toast.error('Fehler beim Generieren des KI-Vorschlags');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const postReply = useCallback(
    async ({ reviewId, comment }) => {
      try {
        return await sendRequest({
          url: `/api/v1/googleReview/${reviewId}/reply`,
          method: 'POST',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: { comment },
        });
      } catch (err) {
        toast.error('Fehler beim Veröffentlichen der Antwort');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const deleteReply = useCallback(
    async ({ reviewId }) => {
      try {
        return await sendRequest({
          url: `/api/v1/googleReview/${reviewId}/reply`,
          method: 'DELETE',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Entfernen der Antwort');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const getLocations = useCallback(async () => {
    try {
      return await sendRequest({
        url: '/api/v1/googleReview/locations',
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
    } catch (err) {
      toast.error('Fehler beim Laden der Google-Standorte');
      throw err;
    }
  }, [authCtx, sendRequest]);

  // Loads the overview of every entertainment with its Google star rating
  // (computed from the synced reviews) plus a match suggestion for unmapped
  // entertainments, and the full list of Google locations for manual choice.
  const getEntertainmentRatings = useCallback(async () => {
    try {
      return await sendRequest({
        url: '/api/v1/googleReview/entertainment-ratings',
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
    } catch (err) {
      toast.error('Fehler beim Laden der Google-Bewertungsübersicht');
      throw err;
    }
  }, [authCtx, sendRequest]);

  // Bulk-assigns Google locations to entertainments. Expects an array of
  // { entertainmentId, googleLocationName } (empty googleLocationName clears).
  const bulkMatchLocations = useCallback(
    async assignments => {
      try {
        return await sendRequest({
          url: '/api/v1/googleReview/match',
          method: 'POST',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: { assignments },
        });
      } catch (err) {
        toast.error('Fehler beim Speichern der Google-Zuordnung');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const refreshLocations = useCallback(async () => {
    try {
      return await sendRequest({
        url: '/api/v1/googleReview/locations/refresh',
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
      });
    } catch (err) {
      toast.error('Fehler beim Aktualisieren der Google-Standorte');
      throw err;
    }
  }, [authCtx, sendRequest]);

  const runFullSync = useCallback(async () => {
    try {
      return await sendRequest({
        url: '/api/v1/googleReview/sync',
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
      });
    } catch (err) {
      toast.error('Fehler beim Starten des Syncs');
      throw err;
    }
  }, [authCtx, sendRequest]);

  const acquireReviewLock = useCallback(
    async reviewId => {
      try {
        return await sendRequest({
          url: `/api/v1/googleReview/${reviewId}/lock`,
          method: 'POST',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
        });
      } catch (err) {
        if (err?.statusCode !== 423 && err?.status !== 423) {
          toast.error('Fehler beim Sperren der Rezension');
        }
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const releaseReviewLock = useCallback(
    async reviewId => {
      try {
        return await sendRequest({
          url: `/api/v1/googleReview/${reviewId}/lock`,
          method: 'DELETE',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const getReviewLockStatus = useCallback(
    async reviewId => {
      try {
        return await sendRequest({
          url: `/api/v1/googleReview/${reviewId}/lock`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  return {
    getReviews,
    getReviewById,
    updateStatus,
    updateInternalNote,
    generateAiSuggestion,
    postReply,
    deleteReply,
    getLocations,
    getEntertainmentRatings,
    bulkMatchLocations,
    refreshLocations,
    runFullSync,
    acquireReviewLock,
    releaseReviewLock,
    getReviewLockStatus,
  };
};

export default useGoogleReview;
